import { useEffect, useRef, useState } from 'react'

import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'

interface DialogConfirmStartPeriodProps extends Omit<DialogProps, 'children'> {
  currentPeriod: string
  isLoading?: boolean
  onConfirm: () => void
}

export default function DialogConfirmStartPeriod({
  currentPeriod,
  onConfirm,
  isLoading = false,
  ...dialogProps
}: DialogConfirmStartPeriodProps) {
  const confirmationText = `Saya setuju untuk mulai Pengelolaan Kinerja periode ${currentPeriod}.`

  const [agreementText, setAgreementText] = useState('')
  const [isValid, setIsValid] = useState(true)
  const [isDirty, setIsDirty] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (dialogProps.isOpen) {
      setAgreementText('')
      setIsDirty(false)
    }
  }, [dialogProps.isOpen])

  const handleConfirm = () => {
    if (agreementText !== confirmationText) {
      setIsValid(false)

      textareaRef.current?.focus()
    } else {
      setIsValid(true)
      onConfirm()
    }
  }

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />

      <DialogContent>
        <div className="p-6 flex flex-col gap-4">
          <div>
            <Text variant="display-sm">Mulai periode {currentPeriod}?</Text>

            <Text
              as="div"
              variant="body-lg"
              color="subdued"
              className="mt-1 !text-xl"
            >
              Jika lanjut:
              <ol className="list-decimal list-outside ml-6">
                <li>Guru dapat mulai perencanaan dan pelaksanaan.</li>
                <li>
                  Tim Kerja (Pengawas Sekolah) dan Dinas Pendidikan dapat mulai
                  pemantauan dan penilaian.
                </li>
              </ol>
            </Text>
          </div>

          <Text variant="body-lg" color="subdued" className="!text-xl">
            Mohon ketik kalimat ini pada kolom kalimat persetujuan:
          </Text>

          <div className="rounded p-1 bg-blue-5">
            <Text
              variant="heading-sm"
              color="informational"
              className="!text-blue-60 select-none"
            >
              {confirmationText}
            </Text>
          </div>

          <div>
            <Text className="mb-1">Kalimat persetujuan</Text>

            <Textarea
              ref={textareaRef}
              onChange={(e) => {
                if (!isDirty) {
                  setIsDirty(true)
                }

                setAgreementText(e.target.value)
              }}
              rows={2}
              isInvalid={!isValid}
              errorMessage={isValid ? '' : 'Mohon cek ejaan dan coba lagi.'}
            ></Textarea>
          </div>

          <div className="flex gap-5">
            <Button
              onClick={() => dialogProps?.onClose?.()}
              color="white"
              size="lg"
            >
              Kembali
            </Button>
            <Button
              disabled={isLoading || !isDirty}
              isLoading={isLoading}
              color="blue"
              size="lg"
              onClick={handleConfirm}
            >
              Lanjut
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
